import { uiPaper, uiSurface } from "shared/ui/uiSurface"
import { useAuth0 } from "@auth0/auth0-react"
import { Check, Close, Edit, Logout, Security } from "@mui/icons-material"
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { FC, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useChangeUsernameMutation,
  useGet2faStatusQuery,
  useRemove2faMutation,
  useResetPasswordMutation,
} from "src/features/profile/profileApi"
import { selectProfile } from "src/features/profile/profileSlice"
import { useAppSelector } from "../../hooks/hooks"
import { TwoFactorSetupDialog } from "./TwoFactorSetupDialog"

const InfoRow: FC<{
  label: string
  value: React.ReactNode
  "data-testid"?: string
}> = ({ label, value, "data-testid": testId }) => (
  <Stack direction="row" spacing={1} data-testid={testId}>
    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
      {label}:
    </Typography>
    <Typography variant="body2">{value ?? "\u2014"}</Typography>
  </Stack>
)

export const ProfilePage: FC = () => {
  const { t } = useTranslation()
  const { logout } = useAuth0()
  const profile = useAppSelector(selectProfile)
  const [resetPassword, { isLoading: resetLoading, isSuccess: resetSent }] =
    useResetPasswordMutation()
  const [changeUsername, { isLoading: usernameLoading }] =
    useChangeUsernameMutation()
  const { data: twoFaStatus, refetch: refetch2fa } = useGet2faStatusQuery()
  const [remove2fa] = useRemove2faMutation()
  const [setupDialogOpen, setSetupDialogOpen] = useState(false)
  const [disableDialogOpen, setDisableDialogOpen] = useState(false)
  const is2faEnrolled = twoFaStatus?.data?.enrolled ?? false

  const firstName = profile?.user?.firstName ?? ""
  const lastName = profile?.user?.lastName ?? ""
  const username = profile?.user?.username ?? ""
  const providerName = profile?.provider?.name ?? ""

  const initials = [firstName[0], lastName[0]]
    .filter(Boolean)
    .join("")
    .toUpperCase()
  const fullName = [firstName, lastName].filter(Boolean).join(" ")

  // Username edit state (F161)
  const [editingUsername, setEditingUsername] = useState(false)
  const [newUsername, setNewUsername] = useState("")
  const [usernameError, setUsernameError] = useState("")
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: "success" | "error"
  }>({ open: false, message: "", severity: "success" })

  const handleUsernameEdit = () => {
    setNewUsername(username)
    setUsernameError("")
    setEditingUsername(true)
  }

  const handleUsernameCancel = () => {
    setEditingUsername(false)
    setNewUsername("")
    setUsernameError("")
  }

  const handleUsernameSaveClick = () => {
    if (!newUsername.trim()) {
      setUsernameError(t("profile.usernameInvalid"))
      return
    }
    if (newUsername.includes(":")) {
      setUsernameError(t("profile.usernameInvalid"))
      return
    }
    if (newUsername === username) {
      handleUsernameCancel()
      return
    }
    setConfirmDialogOpen(true)
  }

  const handleUsernameConfirm = async () => {
    setConfirmDialogOpen(false)
    try {
      await changeUsername({
        currentUsername: username,
        newUsername,
      }).unwrap()
      setSnackbar({
        open: true,
        message: t("profile.usernameChanged"),
        severity: "success",
      })
      setEditingUsername(false)
      // Log out after a short delay so the user sees the success message
      setTimeout(() => {
        logout({
          logoutParams: { returnTo: window.location.origin + "/login" },
        })
      }, 2000)
    } catch (error: any) {
      const msg =
        error?.data?.message || error?.message || "Failed to change username"
      setSnackbar({ open: true, message: msg, severity: "error" })
    }
  }

  const handleDisable2fa = async () => {
    setDisableDialogOpen(false)
    try {
      await remove2fa().unwrap()
      refetch2fa()
      setSnackbar({
        open: true,
        message: t("profile.2fa.disable.success"),
        severity: "success",
      })
    } catch {
      setSnackbar({
        open: true,
        message: "Failed to disable 2FA",
        severity: "error",
      })
    }
  }

  const handleLogout = () => {
    logout({
      logoutParams: { returnTo: window.location.origin + "/login" },
    })
  }

  return (
    <Container maxWidth="sm" sx={{ py: 4 }} data-testid="profile-page">
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        {/* Header */}
        <Stack {...uiSurface("UI-DLV2-161")} alignItems="center" spacing={1.5} sx={{ p: 3, pb: 2 }}>
          <Avatar
            data-testid="profile-avatar"
            sx={{
              width: 80,
              height: 80,
              fontSize: 32,
              bgcolor: "primary.main",
            }}
          >
            {initials}
          </Avatar>
          <Box sx={{ textAlign: "center" }}>
            <Typography variant="h6" data-testid="profile-fullname">
              {fullName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {username}
            </Typography>
          </Box>
        </Stack>

        <Divider />

        {/* Account Information */}
        <Box {...uiSurface("UI-DLV2-162")} sx={{ p: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t("profile.accountInfo")}
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <InfoRow
              label={t("employees.firstName")}
              value={firstName}
              data-testid="profile-detail-firstName"
            />
            <InfoRow
              label={t("employees.lastName")}
              value={lastName}
              data-testid="profile-detail-lastName"
            />
            {/* Username — editable (F161) */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              data-testid="profile-detail-username"
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ minWidth: 140 }}
              >
                {t("employees.username")}:
              </Typography>
              {editingUsername ? (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <TextField
                    size="small"
                    value={newUsername}
                    onChange={(e) => {
                      setNewUsername(e.target.value)
                      setUsernameError("")
                    }}
                    error={!!usernameError}
                    helperText={usernameError}
                    disabled={usernameLoading}
                    sx={{ minWidth: 180 }}
                    autoFocus
                  />
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={handleUsernameSaveClick}
                    disabled={usernameLoading}
                  >
                    <Check fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={handleUsernameCancel}
                    disabled={usernameLoading}
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </Stack>
              ) : (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body2">{username}</Typography>
                  <IconButton size="small" onClick={handleUsernameEdit}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Stack>
              )}
            </Stack>
          </Stack>
        </Box>

        <Divider />

        {/* Security */}
        <Box {...uiSurface("UI-DLV2-163")} sx={{ p: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t("profile.security")}
          </Typography>
          <Stack spacing={1} sx={{ mt: 1 }}>
            {resetSent ? (
              <Alert severity="success" variant="outlined">
                {t("profile.resetPasswordSent")}
              </Alert>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  {t("profile.resetPasswordDesc")}
                </Typography>
                <Box>
                  <Button
                    data-testid="profile-reset-password-btn"
                    variant="outlined"
                    size="small"
                    onClick={() => resetPassword()}
                    disabled={resetLoading}
                    sx={{ textTransform: "none" }}
                  >
                    {t("profile.resetPassword")}
                  </Button>
                </Box>
              </>
            )}
          </Stack>
        </Box>

        <Divider />

        {/* Two-Factor Authentication (F164) */}
        <Box {...uiSurface("UI-DLV2-164")} sx={{ p: 3 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mb: 1 }}
          >
            <Security fontSize="small" color="primary" />
            <Typography variant="subtitle2" color="text.secondary">
              {t("profile.2fa.title")}
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t("profile.2fa.description")}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            {is2faEnrolled ? (
              <>
                <Chip
                  label={t("profile.2fa.enabled")}
                  color="success"
                  size="small"
                  variant="outlined"
                />
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => setDisableDialogOpen(true)}
                  sx={{ textTransform: "none" }}
                >
                  {t("profile.2fa.disable")}
                </Button>
              </>
            ) : (
              <>
                <Chip
                  label={t("profile.2fa.disabled")}
                  color="default"
                  size="small"
                  variant="outlined"
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setSetupDialogOpen(true)}
                  sx={{ textTransform: "none" }}
                >
                  {t("profile.2fa.enable")}
                </Button>
              </>
            )}
          </Stack>
        </Box>

        <Divider />

        {/* Provider */}
        <Box {...uiSurface("UI-DLV2-165")} sx={{ p: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {t("profile.providerSection")}
          </Typography>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            <InfoRow
              label={t("profile.providerName")}
              value={providerName}
              data-testid="profile-detail-providerName"
            />
          </Stack>
        </Box>

        <Divider />

        {/* Logout */}
        <Box {...uiSurface("UI-DLV2-166")} sx={{ p: 3 }}>
          <Button
            data-testid="profile-logout-btn"
            variant="outlined"
            color="error"
            startIcon={<Logout />}
            onClick={handleLogout}
            sx={{ textTransform: "none" }}
          >
            {t("logout")}
          </Button>
        </Box>
      </Paper>

      {/* 2FA Setup Dialog */}
      <TwoFactorSetupDialog
        open={setupDialogOpen}
        onClose={() => setSetupDialogOpen(false)}
        onSuccess={() => {
          refetch2fa()
          setSnackbar({
            open: true,
            message: t("profile.2fa.setup.success"),
            severity: "success",
          })
        }}
      />

      {/* 2FA Disable confirmation */}
      <Dialog
        slotProps={{ paper: uiPaper("UI-DLV2-168") }}
        open={disableDialogOpen}
        onClose={() => setDisableDialogOpen(false)}
      >
        <DialogTitle>{t("profile.2fa.disable")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("profile.2fa.disable.confirm")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDisableDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleDisable2fa}
            color="error"
            variant="contained"
          >
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Username change confirmation dialog */}
      <Dialog slotProps={{ paper: uiPaper("UI-DLV2-169") }} open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>{t("profile.changeUsername")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("profile.confirmUsernameChange")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleUsernameConfirm}
            variant="contained"
            autoFocus
          >
            {t("common.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  )
}
